import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SymbolToolbar from "../../components/SymbolToolbar";
import { MathPreview } from "../../components/MathText";
import { showError, showSuccess, showWarning } from "../../utils/sweetAlert";
import { authFetch, firstError as firstApiError, storageUrl } from "../../utils/api";
import { MATCH_COLUMN_I_KEYS, MATCH_COLUMN_II_KEYS, matchLabel } from "../../utils/questionTypes";
import "./EditQuestion.css";

function EditQuestion() {
  const navigate = useNavigate();
  const { seriesId, sectionId: urlSectionId, questionId } = useParams();
  const textareaRefs = useRef({});
  const lastFocusedTextarea = useRef(null);
  const questionImageInputRef = useRef(null);
  // Image paths the question had when loaded: a cleared preview then means "remove this image"
  const originalImages = useRef({ image: null, paragraph_en_image: null, paragraph_hi_image: null, options: {} });
  const paragraphEngImageInputRef = useRef(null);
  const paragraphHindiImageInputRef = useRef(null);
  const optionImageInputRefs = useRef({});

  const [currentLanguage, setCurrentLanguage] = useState("english");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sectionName, setSectionName] = useState("");
  const [questionType, setQuestionType] = useState("SINGLE_CORRECT");
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [sectionId, setSectionId] = useState(urlSectionId || null);
  const [optionCount, setOptionCount] = useState(4); // Dynamic options: 4-6
  const [matchMappings, setMatchMappings] = useState({}); // For MATCH type: { P: 'A', Q: 'B', ... }
  const hasOptions = ["SINGLE_CORRECT", "MULTI_CORRECT", "PARAGRAPH", "MATCH"].includes(questionType);
  
  // Clean up invalid matchMappings when optionCount changes for MATCH type
  useEffect(() => {
    if (questionType === "MATCH") {
      const columnIIKeys = ['P', 'Q', 'R', 'S', 'T', 'U'];
      const validKeys = columnIIKeys.slice(0, optionCount);
      const newMappings = {};
      
      // Keep only mappings for valid Column II keys
      validKeys.forEach(key => {
        if (matchMappings[key]) {
          newMappings[key] = matchMappings[key];
        }
      });
      
      // Update if mappings changed
      if (Object.keys(newMappings).length !== Object.keys(matchMappings).length) {
        setMatchMappings(newMappings);
      }
    }
  }, [optionCount, questionType, matchMappings]);

  const [formData, setFormData] = useState({
    paragraphEng: "",
    paragraphHindi: "",
    questionEng: "",
    questionHindi: "",
    topic_name: "",
    topic_name_hi: "",
    marks: "",
    negative_marks: "",
    correctAnswer: "A",
    numerical_value: "",
    numerical_ranges: [],
    options: [],
    explanationEng: "",
    explanationHindi: "",
    youtubeLink: "",
    questionImage: null,
    imagePreview: null,
    paragraphEngImage: null,
    paragraphHindiImage: null,
    paragraphEngImagePreview: null,
    paragraphHindiImagePreview: null,
    // Option English images (file objects)
    optionAEnImage: null, optionBEnImage: null, optionCEnImage: null,
    optionDEnImage: null, optionEEnImage: null, optionFEnImage: null,
    optionPEnImage: null, optionQEnImage: null, optionREnImage: null,
    optionSEnImage: null, optionTEnImage: null, optionUEnImage: null,
    // Option Hindi images (file objects)
    optionAHiImage: null, optionBHiImage: null, optionCHiImage: null,
    optionDHiImage: null, optionEHiImage: null, optionFHiImage: null,
    optionPHiImage: null, optionQHiImage: null, optionRHiImage: null,
    optionSHiImage: null, optionTHiImage: null, optionUHiImage: null,
    // Option English image previews
    optionAEnImagePreview: null, optionBEnImagePreview: null, optionCEnImagePreview: null,
    optionDEnImagePreview: null, optionEEnImagePreview: null, optionFEnImagePreview: null,
    optionPEnImagePreview: null, optionQEnImagePreview: null, optionREnImagePreview: null,
    optionSEnImagePreview: null, optionTEnImagePreview: null, optionUEnImagePreview: null,
    // Option Hindi image previews
    optionAHiImagePreview: null, optionBHiImagePreview: null, optionCHiImagePreview: null,
    optionDHiImagePreview: null, optionEHiImagePreview: null, optionFHiImagePreview: null,
    optionPHiImagePreview: null, optionQHiImagePreview: null, optionRHiImagePreview: null,
    optionSHiImagePreview: null, optionTHiImagePreview: null, optionUHiImagePreview: null,
  });

  const [errors, setErrors] = useState({});

  // Fetch section details
  useEffect(() => {
    const fetchSection = async () => {
      if (!sectionId) {
        // console.log('No sectionId available yet, skipping section fetch');
        return;
      }
      
      try {
        // console.log('Fetching section with ID:', sectionId);
        const response = await authFetch(`/fetch-section/${sectionId}`);
        
        const data = await response.json();
        // console.log('Section Response:', data);
        if (response.ok && data.success && data.data) {
          const section = Array.isArray(data.data) ? data.data[0] : data.data;
          setSectionName(section?.name || "");
        }
      } catch (error) {
        // console.error("Error fetching section:", error);
      }
    };

    fetchSection();
  }, [sectionId]);

  // Fetch question details
  useEffect(() => {
    const fetchQuestion = async () => {
      try {
        setIsLoading(true);
        const response = await authFetch(`/questions/${questionId}`);

        const data = await response.json();
        // console.log('Question data:', data);

        if (response.ok && data.success && data.data) {
          const question = data.data;
          
          // Set sectionId from question data if not available from URL
          if (!urlSectionId && question.section_id) {
            // console.log('Setting sectionId from question data:', question.section_id);
            setSectionId(question.section_id);
          } else if (urlSectionId) {
            // console.log('Using sectionId from URL:', urlSectionId);
            setSectionId(urlSectionId);
          }
          
          setQuestionType(question.question_type || "SINGLE_CORRECT");

          // Map options from API response
          const mappedOptions = (question.options || []).map((opt) => ({
            id: opt.id,
            key: opt.option_key,
            option_en: opt.option_en || "",
            option_hi: opt.option_hi || "",
            match_key: opt.match_key || null,
            image: opt.image || null,
            image_en: opt.image_en || opt.option_en_image || null,
            image_hi: opt.image_hi || opt.option_hi_image || null,
          }));

          // Set optionCount based on options length (for SINGLE_CORRECT, MULTI_CORRECT, PARAGRAPH)
          if (mappedOptions.length > 0 && 
              (question.question_type === "SINGLE_CORRECT" || 
               question.question_type === "MULTI_CORRECT" || 
               question.question_type === "PARAGRAPH")) {
            setOptionCount(mappedOptions.length);
          } else if (question.question_type === "MATCH") {
            // For MATCH type, options hold both columns; size by the longer column (4–6)
            const count = Math.min(6, Math.max(
              4,
              mappedOptions.filter(opt => MATCH_COLUMN_I_KEYS.includes(opt.key)).length,
              mappedOptions.filter(opt => MATCH_COLUMN_II_KEYS.includes(opt.key)).length
            ));
            setOptionCount(count);
            
            // Build matchMappings from options with match_key
            const mappings = {};
            mappedOptions.forEach(opt => {
              if (opt.match_key && ['P', 'Q', 'R', 'S', 'T', 'U'].includes(opt.key)) {
                mappings[opt.key] = opt.match_key;
              }
            });
            setMatchMappings(mappings);
            
            // Ensure we have all Column I and Column II options
            const columnIKeys = ['A', 'B', 'C', 'D', 'E', 'F'].slice(0, count);
            const columnIIKeys = ['P', 'Q', 'R', 'S', 'T', 'U'].slice(0, count);
            
            // Check if we need to add missing options
            const existingKeys = mappedOptions.map(opt => opt.key);
            const allRequiredKeys = [...columnIKeys, ...columnIIKeys];
            
            allRequiredKeys.forEach(key => {
              if (!existingKeys.includes(key)) {
                mappedOptions.push({
                  key: key,
                  option_en: "",
                  option_hi: "",
                  match_key: ['P', 'Q', 'R', 'S', 'T', 'U'].includes(key) ? null : undefined,
                  image: null
                });
              }
            });
            
            // Sort options to maintain order (A-F first, then P-U)
            mappedOptions.sort((a, b) => {
              const orderA = ['A', 'B', 'C', 'D', 'E', 'F', 'P', 'Q', 'R', 'S', 'T', 'U'].indexOf(a.key);
              const orderB = ['A', 'B', 'C', 'D', 'E', 'F', 'P', 'Q', 'R', 'S', 'T', 'U'].indexOf(b.key);
              return orderA - orderB;
            });
          }

          // Parse correct answer for multi-correct questions
          const correctAns = question.correct_answer || "A";
          if (question.question_type === "MULTI_CORRECT") {
            // Convert "ABC" or "A,B,C" to array ["A", "B", "C"]
            const answersArray = correctAns.includes(",") 
              ? correctAns.split(",").map(a => a.trim())
              : correctAns.split("").filter(a => a.trim());
            setSelectedAnswers(answersArray);
          }

          originalImages.current = {
            image: question.image || null,
            paragraph_en_image: question.paragraph_en_image || null,
            paragraph_hi_image: question.paragraph_hi_image || null,
            options: Object.fromEntries(
              mappedOptions.map((opt) => [opt.key, { en: opt.image_en || opt.image || null, hi: opt.image_hi || null }])
            ),
          };

          // Set option image previews from API (language-specific)
          const optionImagePreviews = {};
          mappedOptions.forEach(opt => {
            // English image: prefer image_en, then fall back to generic image
            const enSrc = opt.image_en || opt.image;
            if (enSrc) {
              optionImagePreviews[`option${opt.key}EnImagePreview`] = storageUrl(enSrc);
            }
            // Hindi image
            if (opt.image_hi) {
              optionImagePreviews[`option${opt.key}HiImagePreview`] = storageUrl(opt.image_hi);
            }
          });

          setFormData({
            paragraphEng: question.paragraph_en || "",
            paragraphHindi: question.paragraph_hi || "",
            questionEng: question.question_en || "",
            questionHindi: question.question_hi || "",
            topic_name: question.topic_name || "",
            topic_name_hi: question.topic_name_hi || "",
            marks: question.marks || "",
            negative_marks: question.negative_marks || "",
            correctAnswer: correctAns,
            numerical_value: question.numerical_value ?? "",
            numerical_ranges: Array.isArray(question.numerical_ranges) ? question.numerical_ranges : [],
            options: mappedOptions.length > 0 ? mappedOptions : [
              { key: "A", option_en: "", option_hi: "", match_key: null },
              { key: "B", option_en: "", option_hi: "", match_key: null },
              { key: "C", option_en: "", option_hi: "", match_key: null },
              { key: "D", option_en: "", option_hi: "", match_key: null },
            ],
            explanationEng: question.explanation_en || "",
            explanationHindi: question.explanation_hi || "",
            youtubeLink: question.youtube_link || "",
            questionImage: null,
            imagePreview: storageUrl(question.image),
            paragraphEngImage: null,
            paragraphHindiImage: null,
            paragraphEngImagePreview: storageUrl(question.paragraph_en_image),
            paragraphHindiImagePreview: storageUrl(question.paragraph_hi_image),
            // Option image files (null — new uploads only; previews loaded above)
            // Set option image previews from API
            ...optionImagePreviews,
          });
        } else {
          showError(data.message || "Failed to fetch question");
          if (sectionId) {
            navigate(`/admin/test-series/${seriesId}/section/${sectionId}/questions`);
          } else {
            navigate(`/admin/test-series`);
          }
        }
      } catch (error) {
        // console.error("Error fetching question:", error);
        showError("Error loading question: " + error.message);
        if (sectionId) {
          navigate(`/admin/test-series/${seriesId}/section/${sectionId}/questions`);
        } else {
          navigate(`/admin/test-series`);
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (questionId) {
      fetchQuestion();
    }
    // sectionId is set by this effect and only read in the error redirect — re-running on it would refetch needlessly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId, seriesId, urlSectionId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleCheckboxChange = (optionKey) => {
    setSelectedAnswers((prev) => {
      if (prev.includes(optionKey)) {
        // Remove if already selected
        return prev.filter((key) => key !== optionKey);
      } else {
        // Add if not selected
        return [...prev, optionKey].sort();
      }
    });
  };

  const handleOptionChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.map((opt, i) => (i === index ? { ...opt, [field]: value } : opt)),
    }));
    const errorKey = `option_${index}_${field === "option_en" ? "en" : "hi"}`;
    if (errors[errorKey]) setErrors((prev) => ({ ...prev, [errorKey]: null }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          questionImage: file,
          imagePreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    if (questionImageInputRef.current) {
      questionImageInputRef.current.value = "";
    }
    setFormData({
      ...formData,
      questionImage: null,
      imagePreview: null,
    });
  };

  const handleParagraphImageUpload = (field) => (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          [field]: file,
          [`${field}Preview`]: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeParagraphImage = (field) => {
    if (field === "paragraphEngImage" && paragraphEngImageInputRef.current) {
      paragraphEngImageInputRef.current.value = "";
    }
    if (field === "paragraphHindiImage" && paragraphHindiImageInputRef.current) {
      paragraphHindiImageInputRef.current.value = "";
    }
    setFormData({
      ...formData,
      [field]: null,
      [`${field}Preview`]: null,
    });
  };

  // Handle option image upload
  const handleOptionImageUpload = (optionKey, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 250 * 1024; // 250 KB
    if (file.size > maxSize) {
      showError("Option image must be smaller than 250 KB");
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    const langSuffix = currentLanguage === "english" ? "En" : "Hi";
    reader.onloadend = () => {
      setFormData((prev) => ({
        ...prev,
        [`option${optionKey}${langSuffix}Image`]: file,
        [`option${optionKey}${langSuffix}ImagePreview`]: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  // Remove option image
  const removeOptionImage = (optionKey) => {
    const langSuffix = currentLanguage === "english" ? "En" : "Hi";
    const refKey = `${optionKey}_${currentLanguage === "english" ? "en" : "hi"}`;
    if (optionImageInputRefs.current[refKey]) {
      optionImageInputRefs.current[refKey].value = "";
    }
    setFormData({
      ...formData,
      [`option${optionKey}${langSuffix}Image`]: null,
      [`option${optionKey}${langSuffix}ImagePreview`]: null,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.questionEng.trim()) {
      newErrors.questionEng = "English question text is required";
    }
    // Hindi question is now optional - no validation needed

    if (!formData.marks || formData.marks <= 0) {
      newErrors.marks = "Marks are required and must be greater than 0";
    }

    // Validate based on question type
    if (questionType === "NUMERICAL") {
      if (formData.numerical_value === "" || formData.numerical_value == null) {
        newErrors.numerical_value = "Numerical value is required";
      }
    } else if (questionType === "RANGE") {
      if (!String(formData.correctAnswer || "").trim()) {
        newErrors.correctAnswer = "Range answer is required";
      }
    } else if (questionType === "PARAGRAPH") {
      // Validate paragraph text
      if (!formData.paragraphEng.trim()) {
        newErrors.paragraphEng = "English paragraph/comprehension text is required";
      }
      // Validate options
      formData.options.slice(0, optionCount).forEach((option, index) => {
        if (!option.option_en.trim()) {
          newErrors[`option_${index}_en`] = `Option ${option.key} (English) is required`;
        }
      });

      if (formData.options.length < 2) {
        newErrors.options = "At least 2 options are required";
      }
    } else if (questionType === "MULTI_CORRECT") {
      // Multi correct validation
      formData.options.slice(0, optionCount).forEach((option, index) => {
        if (!option.option_en.trim()) {
          newErrors[`option_${index}_en`] = `Option ${option.key} (English) is required`;
        }
      });

      if (formData.options.length < 2) {
        newErrors.options = "At least 2 options are required";
      }
      
      if (selectedAnswers.length === 0) {
        newErrors.correctAnswers = "Please select at least one correct answer";
      }
    } else if (questionType === "MATCH") {
      // Validate Column I and Column II options
      const columnIOptions = formData.options.filter(opt => ['A', 'B', 'C', 'D', 'E', 'F'].includes(opt.key)).slice(0, optionCount);
      const columnIIOptions = formData.options.filter(opt => ['P', 'Q', 'R', 'S', 'T', 'U'].includes(opt.key)).slice(0, optionCount);
      
      columnIOptions.forEach((option, index) => {
        if (!option.option_en.trim()) {
          newErrors[`option_${index}_en`] = `Column I Item ${matchLabel(option.key)} (English) is required`;
        }
      });
      
      columnIIOptions.forEach((option, index) => {
        if (!option.option_en.trim()) {
          newErrors[`option_${optionCount + index}_en`] = `Column II Item ${matchLabel(option.key)} (English) is required`;
        }
      });
      
      // Validate match mappings
      const requiredColumnIIKeys = ['P', 'Q', 'R', 'S', 'T', 'U'].slice(0, optionCount);
      for (const key of requiredColumnIIKeys) {
        if (!matchMappings[key]) {
          newErrors.matchMappings = "Please map all Column II items to Column I items";
          break;
        }
      }
    } else {
      // For SINGLE_CORRECT
      formData.options.slice(0, optionCount).forEach((option, index) => {
        if (!option.option_en.trim()) {
          newErrors[`option_${index}_en`] = `Option ${option.key} (English) is required`;
        }
      });

      if (formData.options.length < 2) {
        newErrors.options = "At least 2 options are required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!sectionId) {
      showError("Section ID is missing. Cannot update question.");
      return;
    }

    if (!validateForm()) {
      // Required fields live on the English tab — switch there so errors are visible
      setCurrentLanguage("english");
      showWarning("Some required fields are missing. They're highlighted in the form.", "Please complete the question");
      setTimeout(() => {
        const firstError = document.querySelector(".eq-container .error, .eq-match-error");
        if (firstError) firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 350);
      return;
    }

    setIsSubmitting(true);

    try {
      // console.log('Submitting with sectionId:', sectionId);
      const formDataToSend = new FormData();

      // Required fields
      formDataToSend.append("section_id", sectionId);
      formDataToSend.append("question_type", questionType);
      formDataToSend.append("question_en", formData.questionEng);
      formDataToSend.append("question_hi", formData.questionHindi);
      formDataToSend.append("marks", formData.marks);

      // Optional fields
      if (formData.topic_name.trim()) {
        formDataToSend.append("topic_name", formData.topic_name);
      }
      // Always sent: update replaces the whole question, so leaving it out would clear a saved Hindi topic
      formDataToSend.append("topic_name_hi", formData.topic_name_hi.trim());
      if (formData.negative_marks) {
        formDataToSend.append("negative_marks", formData.negative_marks);
      }
      if (formData.explanationEng.trim()) {
        formDataToSend.append("explanation_en", formData.explanationEng);
      }
      if (formData.explanationHindi.trim()) {
        formDataToSend.append("explanation_hi", formData.explanationHindi);
      }

      // One option. Images use the field names the API stores (option_en_image / option_hi_image);
      // an image the admin cleared is sent as remove_option_*_image so the server deletes it.
      const appendOption = (index, option, matchKey) => {
        if (option.id) {
          formDataToSend.append(`options[${index}][id]`, option.id);
        }
        formDataToSend.append(`options[${index}][key]`, option.key);
        formDataToSend.append(`options[${index}][option_en]`, option.option_en);
        if (option.option_hi) {
          formDataToSend.append(`options[${index}][option_hi]`, option.option_hi);
        }
        if (matchKey) {
          formDataToSend.append(`options[${index}][match_key]`, matchKey);
        }
        [["En", "en"], ["Hi", "hi"]].forEach(([suffix, lang]) => {
          const file = formData[`option${option.key}${suffix}Image`];
          const hadImage = originalImages.current.options[option.key]?.[lang];
          if (file) {
            formDataToSend.append(`options[${index}][option_${lang}_image]`, file);
          } else if (hadImage && !formData[`option${option.key}${suffix}ImagePreview`]) {
            formDataToSend.append(`options[${index}][remove_option_${lang}_image]`, "1");
          }
        });
      };

      // Question type specific fields
      if (questionType === "RANGE") {
        formDataToSend.append("correct_answer", String(formData.correctAnswer).trim());
      } else if (questionType === "NUMERICAL") {
        formDataToSend.append("numerical_value", formData.numerical_value);
        // Ranges as a list of { min, max }, the same shape the API stores; older rows may hold "2-5"
        (formData.numerical_ranges || []).forEach((range, index) => {
          if (range && typeof range === "object") {
            formDataToSend.append(`numerical_ranges[${index}][min]`, range.min ?? "");
            formDataToSend.append(`numerical_ranges[${index}][max]`, range.max ?? "");
          } else if (range !== null && range !== undefined && String(range).trim()) {
            formDataToSend.append(`numerical_ranges[${index}]`, String(range).trim());
          }
        });
      } else if (questionType === "PARAGRAPH") {
        // Include paragraph/comprehension text
        if (formData.paragraphEng.trim()) {
          formDataToSend.append("paragraph_en", formData.paragraphEng);
        }
        if (formData.paragraphHindi.trim()) {
          formDataToSend.append("paragraph_hi", formData.paragraphHindi);
        }

        formDataToSend.append("correct_answer", formData.correctAnswer);
        formData.options.forEach((option, index) => appendOption(index, option));
      } else if (questionType === "SINGLE_CORRECT") {
        formDataToSend.append("correct_answer", formData.correctAnswer);
        formData.options.forEach((option, index) => appendOption(index, option));
      } else if (questionType === "MULTI_CORRECT") {
        // Multi correct answers as comma-separated string
        formDataToSend.append("correct_answer", selectedAnswers.join(","));
        formData.options.forEach((option, index) => appendOption(index, option));
      } else if (questionType === "MATCH") {
        // Column I (A–F) first, then Column II (P–U) with the Column I key each one matches
        const columnIOptions = formData.options.filter(opt => ['A', 'B', 'C', 'D', 'E', 'F'].includes(opt.key));
        const columnIIOptions = formData.options.filter(opt => ['P', 'Q', 'R', 'S', 'T', 'U'].includes(opt.key));

        columnIOptions.forEach((option, index) => appendOption(index, option));
        columnIIOptions.forEach((option, index) =>
          appendOption(columnIOptions.length + index, option, matchMappings[option.key] || "")
        );
      }

      // Image upload, or remove the current image when its preview was cleared
      if (formData.questionImage) {
        formDataToSend.append("image", formData.questionImage);
      } else if (originalImages.current.image && !formData.imagePreview) {
        formDataToSend.append("remove_image", "1");
      }

      // Paragraph images (for PARAGRAPH type)
      if (questionType === "PARAGRAPH") {
        if (formData.paragraphEngImage) {
          formDataToSend.append("paragraph_en_image", formData.paragraphEngImage);
        } else if (originalImages.current.paragraph_en_image && !formData.paragraphEngImagePreview) {
          formDataToSend.append("remove_paragraph_en_image", "1");
        }
        if (formData.paragraphHindiImage) {
          formDataToSend.append("paragraph_hi_image", formData.paragraphHindiImage);
        } else if (originalImages.current.paragraph_hi_image && !formData.paragraphHindiImagePreview) {
          formDataToSend.append("remove_paragraph_hi_image", "1");
        }
      }

      // Optional YouTube solution link
      if (formData.youtubeLink && formData.youtubeLink.trim()) {
        formDataToSend.append("youtube_link", formData.youtubeLink.trim());
      }

      // Console log all form data being sent to API
      // console.log('=== FormData being sent to API ===');
      // for (let [key, value] of formDataToSend.entries()) {
      //   if (value instanceof File) {
      //     console.log(`${key}:`, `File(${value.name}, ${value.size} bytes)`);
      //   } else {
      //     console.log(`${key}:`, value);
      //   }
      // }
      // console.log('=== End FormData ===');

      const response = await authFetch(`/questions/update/${questionId}`, {
        method: "POST",
        headers: { "X-HTTP-Method-Override": "POST" },
        body: formDataToSend,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showSuccess("Your changes to this question have been saved.", "Question updated");
        navigate(
          `/admin/test-series/${seriesId}/section/${sectionId}/questions`
        );
      } else {
        throw new Error(firstApiError(data, data.error || "Failed to update question"));
      }
    } catch (error) {
      // console.error("Error updating question:", error);
      showError(error.message, "Couldn't update the question");
    } finally {
      setIsSubmitting(false);
    }
  };

  const insertText = (text) => {
    // Try to get the currently focused element first, otherwise use the last focused textarea
    let targetElement = document.activeElement;
    if (!targetElement || targetElement.tagName !== "TEXTAREA") {
      targetElement = lastFocusedTextarea.current;
    }

    if (targetElement && targetElement.tagName === "TEXTAREA") {
      const start = targetElement.selectionStart;
      const end = targetElement.selectionEnd;
      const fieldName = targetElement.name;
      const currentValue = formData[fieldName] || "";

      // Insert text without wrapping - user can wrap later with wrapper buttons
      const newValue =
        currentValue.substring(0, start) + text + currentValue.substring(end);

      setFormData({
        ...formData,
        [fieldName]: newValue,
      });

      // Set cursor position after inserted equation
      setTimeout(() => {
        targetElement.focus();
        const cursorPos = start + text.length;
        targetElement.setSelectionRange(cursorPos, cursorPos);
      }, 10);
    } else {
      showWarning("Please click in a text field first, then select a symbol.");
    }
  };

  // Helper function to render field with preview
  const renderFieldWithPreview = (fieldName, label, rows = 3) => {
    const value = formData[fieldName] || "";
    const error = errors[fieldName];

    return (
      <div className="eq-form-group">
        <label className="eq-label">{label}</label>
        <textarea
          ref={(el) => (textareaRefs.current[fieldName] = el)}
          name={fieldName}
          value={value}
          onChange={handleChange}
          onFocus={(e) => {
            lastFocusedTextarea.current = e.target;
          }}
          rows={rows}
          className={`eq-textarea${error ? " error" : ""}`}
          placeholder={`Enter ${label.toLowerCase()}... Use \\( \\) for equations`}
        />
        {error && <span className="eq-error-msg">{error}</span>}
        <MathPreview text={value} />
      </div>
    );
  };

  // Render option field with image upload
  const renderOptionWithImage = (option, label, rows = 2, displayKey = option.key) => {
    const optionKey = option.key;
    const value = option[currentLanguage === "english" ? "option_en" : "option_hi"] || "";
    const index = formData.options.findIndex(opt => opt.key === optionKey);
    const error = errors[`option_${index}_${currentLanguage === "english" ? "en" : "hi"}`];
    const langSuffix = currentLanguage === "english" ? "En" : "Hi";
    const refKey = `${optionKey}_${currentLanguage === "english" ? "en" : "hi"}`;
    const imagePreview = formData[`option${optionKey}${langSuffix}ImagePreview`];

    return (
      <div className="eq-option-card">
        <span className="eq-option-card-label">{displayKey}</span>
        <div className="eq-option-text-col">
          <textarea
            ref={(el) => (textareaRefs.current[`option_${optionKey}_text`] = el)}
            value={value}
            onChange={(e) =>
              handleOptionChange(index, currentLanguage === "english" ? "option_en" : "option_hi", e.target.value)
            }
            onFocus={(e) => {
              lastFocusedTextarea.current = e.target;
            }}
            rows={rows}
            className={`eq-textarea${error ? " error" : ""}`}
            placeholder={`Enter ${label.toLowerCase()}... Use \\( \\) for equations`}
          />
          {error && <span className="eq-error-msg">{error}</span>}
          {value && <MathPreview text={value} />}
        </div>
        <div className="eq-option-image-col">
          <span className="eq-option-image-hint">Optional Image</span>
          {imagePreview ? (
            <div className="eq-image-preview-wrap">
              <img src={imagePreview} alt={`${label} preview`} />
              <button
                type="button"
                className="eq-image-remove-btn"
                onClick={() => removeOptionImage(optionKey)}
                title="Remove image"
              >
                <i className="bi bi-x"></i>
              </button>
            </div>
          ) : (
            <input
              ref={(el) => (optionImageInputRefs.current[refKey] = el)}
              type="file"
              accept="image/*"
              onChange={(e) => handleOptionImageUpload(optionKey, e)}
              className="eq-file-input"
            />
          )}
        </div>
      </div>
    );
  };

  // Helper function to add/remove options dynamically
  const addOption = () => {
    if (optionCount >= 6) return;
    const newOptionCount = optionCount + 1;
    setOptionCount(newOptionCount);
    
    if (questionType === "MATCH") {
      // For MATCH type, add both Column I and Column II options
      const columnIKey = String.fromCharCode(64 + newOptionCount); // E or F
      const columnIIKey = ['P', 'Q', 'R', 'S', 'T', 'U'][newOptionCount - 1]; // T or U
      
      const newOptions = [
        ...formData.options,
        { key: columnIKey, option_en: "", option_hi: "", match_key: null },
        { key: columnIIKey, option_en: "", option_hi: "", match_key: null }
      ];
      setFormData({ ...formData, options: newOptions });
    } else {
      // For other types, add only one option
      const newKey = String.fromCharCode(64 + newOptionCount); // E or F
      const newOptions = [...formData.options, { 
        key: newKey, 
        option_en: "", 
        option_hi: "", 
        match_key: null 
      }];
      setFormData({ ...formData, options: newOptions });
    }
  };

  const removeOption = () => {
    if (optionCount <= 4) return;
    const newOptionCount = optionCount - 1;
    setOptionCount(newOptionCount);
    
    if (questionType === "MATCH") {
      // For MATCH type, remove both Column I and Column II options
      // Remove the last Column I option (highest letter) and last Column II option (highest letter)
      const columnIKeys = ['A', 'B', 'C', 'D', 'E', 'F'];
      const columnIIKeys = ['P', 'Q', 'R', 'S', 'T', 'U'];
      const keyToRemoveI = columnIKeys[optionCount - 1]; // E.g., 'F' when optionCount is 6
      const keyToRemoveII = columnIIKeys[optionCount - 1]; // E.g., 'U' when optionCount is 6
      
      const newOptions = formData.options.filter(
        opt => opt.key !== keyToRemoveI && opt.key !== keyToRemoveII
      );
      setFormData({ ...formData, options: newOptions });
      
      // Clean up matchMappings for removed Column II key
      const newMappings = { ...matchMappings };
      delete newMappings[keyToRemoveII];
      setMatchMappings(newMappings);
    } else {
      // For other types, remove last option
      const newOptions = formData.options.slice(0, newOptionCount);
      setFormData({ ...formData, options: newOptions });
    }
  };

  if (isLoading) {
    return (
      <div className="eq-container">
      <div className="eq-loading">
        <div className="eq-loading-spinner" />
        <p>Loading question...</p>
      </div>
    </div>
  );
}

return (
  <div className="eq-container">
      <div className="page-header">
        <div>
          <button
            type="button"
            className="back-link"
            onClick={() => {
              if (sectionId) {
                navigate(`/admin/test-series/${seriesId}/section/${sectionId}/questions`);
              } else {
                navigate(`/admin/test-series`);
              }
            }}
          >
            <i className="bi bi-arrow-left"></i> Questions
          </button>
          <h2>Edit question</h2>
          <p>Update the question text, options and answer. Changes apply as soon as you save.</p>
          {sectionName && (
            <div className="eq-header-meta">
              <span className="badge badge-brand">
                <i className="bi bi-layers"></i> {sectionName}
              </span>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="eq-form-card">
        {/* Controls: Language */}
        <div className="eq-controls">
          <div className="eq-lang-switcher">
            <button
              type="button"
              className={`eq-lang-btn${currentLanguage === "english" ? " active" : ""}`}
              onClick={() => setCurrentLanguage("english")}
            >
              English
            </button>
            <button
              type="button"
              className={`eq-lang-btn${currentLanguage === "hindi" ? " active" : ""}`}
              onClick={() => setCurrentLanguage("hindi")}
            >
              हिंदी
            </button>
          </div>
        </div>

        {/* Question Type */}
        <div className="eq-form-group">
          <label className="eq-label">Question Type</label>
          <div className="eq-question-type-badge">
            {questionType === "SINGLE_CORRECT" && "Single Correct (MCQ)"}
            {questionType === "MULTI_CORRECT" && "Multi Correct"}
            {questionType === "NUMERICAL" && "Numerical"}
            {questionType === "MATCH" && "Match the Following"}
            {questionType === "PARAGRAPH" && "Paragraph / Comprehension"}
            {questionType === "RANGE" && "Range"}
          </div>
        </div>

        {/* Topic Name */}
        <div className="eq-form-group">
          <label className="eq-label">
            Topic Name <span className="eq-label-optional">(optional)</span>
          </label>
          <input
            type="text"
            name="topic_name"
            value={formData.topic_name}
            onChange={handleChange}
            placeholder="e.g., Integration, Organic Chemistry"
            className="eq-input"
          />
        </div>

        {/* Topic Name (Hindi) */}
        <div className="eq-form-group">
          <label className="eq-label">
            Topic Name (Hindi) <span className="eq-label-optional">(optional)</span>
          </label>
          <input
            type="text"
            name="topic_name_hi"
            value={formData.topic_name_hi}
            onChange={handleChange}
            placeholder="e.g., समाकलन, कार्बनिक रसायन"
            maxLength={255}
            className="eq-input"
          />
        </div>

        {/* Marks */}
        <div className="eq-form-row">
          <div className="eq-form-group" style={{marginBottom: 0}}>
            <label className="eq-label">
              Marks <span className="eq-required">*</span>
            </label>
            <input
              type="number"
              name="marks"
              value={formData.marks}
              onChange={handleChange}
              placeholder="e.g., 4"
              className={`eq-input${errors.marks ? " error" : ""}`}
              min="0"
              step="0.01"
            />
            {errors.marks && <span className="eq-error-msg">{errors.marks}</span>}
          </div>
          <div className="eq-form-group" style={{marginBottom: 0}}>
            <label className="eq-label">
              Negative Marks <span className="eq-label-optional">(optional)</span>
            </label>
            <input
              type="number"
              name="negative_marks"
              value={formData.negative_marks}
              onChange={handleChange}
              placeholder="e.g., 1"
              className="eq-input"
              min="0"
              step="0.01"
            />
          </div>
        </div>

        {/* Maths / physics / chemistry symbol palette */}
        <SymbolToolbar onInsert={insertText} sectionName={sectionName} />

        {/* English Fields */}
        {currentLanguage === "english" && (
          <>
            {/* Paragraph field for PARAGRAPH type */}
            {questionType === "PARAGRAPH" && (
              <>
                {renderFieldWithPreview("paragraphEng", "Paragraph/Comprehension Text (English)", 8)}
                <div className="eq-form-group">
                  <label className="eq-label">
                    Paragraph Image (English) <span className="eq-label-optional">(optional)</span>
                  </label>
                  {formData.paragraphEngImagePreview ? (
                    <div className="eq-para-image-preview">
                      <img src={formData.paragraphEngImagePreview} alt="Paragraph English preview" />
                      <button
                        type="button"
                        className="eq-btn eq-btn-danger eq-btn-sm"
                        onClick={() => removeParagraphImage("paragraphEngImage")}
                      >
                        <i className="bi bi-trash3"></i> Remove image
                      </button>
                    </div>
                  ) : (
                    <input
                      ref={paragraphEngImageInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleParagraphImageUpload("paragraphEngImage")}
                      className="eq-file-input"
                    />
                  )}
                </div>
              </>
            )}

            {renderFieldWithPreview("questionEng", "Question (English)", 4)}
            
            {/* Options for MCQ/MULTI_CORRECT/MATCH/PARAGRAPH */}
            {hasOptions && (
              <>
                {/* Dynamic Option Count Controls for non-MATCH types */}
                {(questionType === "SINGLE_CORRECT" || 
                  questionType === "MULTI_CORRECT" || 
                  questionType === "PARAGRAPH") && (
                  <div className="eq-option-control">
                    <span className="eq-option-control-label">Number of Options (4–6)</span>
                    <button
                      type="button"
                      onClick={removeOption}
                      disabled={optionCount <= 4}
                      className="eq-btn eq-btn-secondary eq-btn-sm"
                    >
                      − Remove
                    </button>
                    <span className="eq-option-count-display">
                      {optionCount} Options &nbsp;(A–{String.fromCharCode(64 + optionCount)})
                    </span>
                    <button
                      type="button"
                      onClick={addOption}
                      disabled={optionCount >= 6}
                      className="eq-btn eq-btn-secondary eq-btn-sm"
                    >
                      + Add
                    </button>
                  </div>
                )}

                {questionType === "MATCH" ? (
                  <>
                    {/* Match the Following - Two Columns */}
                    <div style={{marginBottom: "20px"}}>
                      <h3 className="eq-match-column-header">Column I — Items to Match</h3>
                      <div className="eq-option-control" style={{marginBottom: "14px"}}>
                        <span className="eq-option-control-label">Items in Column I (4–6)</span>
                        <button
                          type="button"
                          onClick={removeOption}
                          disabled={optionCount <= 4}
                          className="eq-btn eq-btn-secondary eq-btn-sm"
                        >
                          − Remove
                        </button>
                        <span className="eq-option-count-display">
                          {optionCount} Items &nbsp;({matchLabel("A")}–{matchLabel(MATCH_COLUMN_I_KEYS[optionCount - 1])})
                        </span>
                        <button
                          type="button"
                          onClick={addOption}
                          disabled={optionCount >= 6}
                          className="eq-btn eq-btn-secondary eq-btn-sm"
                        >
                          + Add
                        </button>
                      </div>
                      {formData.options
                        .filter(opt => ['A', 'B', 'C', 'D', 'E', 'F'].includes(opt.key))
                        .slice(0, optionCount)
                        .map(option => renderOptionWithImage(option, `Column I - Item ${matchLabel(option.key)}`, 2, matchLabel(option.key)))
                      }
                    </div>

                    <div style={{marginBottom: "20px"}}>
                      <h3 className="eq-match-column-header">Column II — Options to Match With</h3>
                      <div className="eq-match-tip">
                        <i className="bi bi-lightbulb"></i> <strong>Tip:</strong> Use the dropdown on the right of each Column II item to select which Column I item it matches with.
                      </div>
                      {formData.options
                        .filter(opt => ['P', 'Q', 'R', 'S', 'T', 'U'].includes(opt.key))
                        .slice(0, optionCount)
                        .map((option, i) => {
                          const columnIKeys = formData.options
                            .filter(opt => ['A', 'B', 'C', 'D', 'E', 'F'].includes(opt.key))
                            .slice(0, optionCount)
                            .map(opt => opt.key);
                          
                          // Get already used Column I items (excluding current selection)
                          const usedColumnIKeys = Object.entries(matchMappings)
                            .filter(([colIIKey, colIKey]) => colIIKey !== option.key && colIKey)
                            .map(([_, colIKey]) => colIKey);
                          
                          // Available options: current selection + unused items
                          const availableColumnIKeys = columnIKeys.filter(
                            colIKey => !usedColumnIKeys.includes(colIKey)
                          );
                          
                          return (
                            <div key={option.key} className="eq-match-row">
                              <div className="eq-match-row-option">
                                {renderOptionWithImage(option, `Column II - Item ${matchLabel(option.key)}`, 2, matchLabel(option.key))}
                              </div>
                              <div className="eq-match-dropdown-col">
                                <span className="eq-match-dropdown-label">Matches with:</span>
                                <select
                                  value={matchMappings[option.key] || ""}
                                  onChange={(e) => {
                                    setMatchMappings({
                                      ...matchMappings,
                                      [option.key]: e.target.value
                                    });
                                    if (errors.matchMappings) {
                                      setErrors({ ...errors, matchMappings: null });
                                    }
                                  }}
                                  className={`eq-match-select${matchMappings[option.key] ? " mapped" : ""}`}
                                >
                                  <option value="">Select...</option>
                                  {availableColumnIKeys.map(colIKey => (
                                    <option key={colIKey} value={colIKey}>
                                      Item {matchLabel(colIKey)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          );
                        })
                      }
                      {errors.matchMappings && (
                        <div className="eq-match-error">{errors.matchMappings}</div>
                      )}
                      {Object.keys(matchMappings).length > 0 && (
                        <div className="eq-match-mappings">
                          <strong>Current Mappings:</strong>
                          <div className="eq-match-mapping-chips">
                            {Object.entries(matchMappings)
                              .filter(([, colI]) => colI)
                              .sort(([, a], [, b]) => a.localeCompare(b))
                              .map(([colII, colI]) => (
                                <span key={colII} className="eq-match-chip">
                                  {matchLabel(colI)} → {matchLabel(colII)}
                                </span>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Regular options for SINGLE_CORRECT, MULTI_CORRECT, PARAGRAPH */}
                    <div className="eq-form-group">
                      <label className="eq-label">Options</label>
                    </div>
                    {formData.options
                      .slice(0, optionCount)
                      .map(option => renderOptionWithImage(option, `Option ${option.key}`, 2))
                    }
                  </>
                )}
              </>
            )}

            {/* Numerical Value */}
            {questionType === "NUMERICAL" && (
              <div className="eq-form-group">
                <label className="eq-label">
                  Numerical Value <span className="eq-required">*</span>
                </label>
                <input
                  type="number"
                  name="numerical_value"
                  value={formData.numerical_value}
                  onChange={handleChange}
                  placeholder="e.g., 3.14"
                  className={`eq-input${errors.numerical_value ? " error" : ""}`}
                  step="any"
                />
                {errors.numerical_value && (
                  <span className="eq-error-msg">{errors.numerical_value}</span>
                )}
              </div>
            )}

            {/* Range Answer */}
            {questionType === "RANGE" && (
              <div className="eq-form-group">
                <label className="eq-label">
                  Range Answer <span className="eq-required">*</span>
                </label>
                <input
                  type="text"
                  name="correctAnswer"
                  value={formData.correctAnswer}
                  onChange={handleChange}
                  placeholder="e.g., 2-5 or 3.5"
                  className={`eq-input${errors.correctAnswer ? " error" : ""}`}
                />
                {errors.correctAnswer && <span className="eq-error-msg">{errors.correctAnswer}</span>}
                <span className="eq-hint">Enter the accepted range (e.g. 2-5)</span>
              </div>
            )}

            {renderFieldWithPreview("explanationEng", "Explanation (English) (Optional)", 5)}
          </>
        )}

        {/* Hindi Fields */}
        {currentLanguage === "hindi" && (
          <>
            {/* Paragraph field for PARAGRAPH type */}
            {questionType === "PARAGRAPH" && (
              <>
                {renderFieldWithPreview("paragraphHindi", "पैराग्राफ/बोधगम्यता पाठ (हिंदी)", 8)}
                <div className="eq-form-group">
                  <label className="eq-label">
                    Paragraph Image (Hindi) <span className="eq-label-optional">(optional)</span>
                  </label>
                  {formData.paragraphHindiImagePreview ? (
                    <div className="eq-para-image-preview">
                      <img src={formData.paragraphHindiImagePreview} alt="Paragraph Hindi preview" />
                      <button
                        type="button"
                        className="eq-btn eq-btn-danger eq-btn-sm"
                        onClick={() => removeParagraphImage("paragraphHindiImage")}
                      >
                        <i className="bi bi-trash3"></i> Remove image
                      </button>
                    </div>
                  ) : (
                    <input
                      ref={paragraphHindiImageInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleParagraphImageUpload("paragraphHindiImage")}
                      className="eq-file-input"
                    />
                  )}
                </div>
              </>
            )}

            {renderFieldWithPreview("questionHindi", "प्रश्न (हिंदी)", 4)}
            
            {/* Options for MCQ/MULTI_CORRECT/MATCH/PARAGRAPH */}
            {hasOptions && (
              <>
                {/* Dynamic Option Count Controls for non-MATCH types */}
                {(questionType === "SINGLE_CORRECT" || 
                  questionType === "MULTI_CORRECT" || 
                  questionType === "PARAGRAPH") && (
                  <div className="eq-option-control">
                    <span className="eq-option-control-label">विकल्पों की संख्या (4–6)</span>
                    <button
                      type="button"
                      onClick={removeOption}
                      disabled={optionCount <= 4}
                      className="eq-btn eq-btn-secondary eq-btn-sm"
                    >
                      − हटाएं
                    </button>
                    <span className="eq-option-count-display">
                      {optionCount} विकल्प &nbsp;(A–{String.fromCharCode(64 + optionCount)})
                    </span>
                    <button
                      type="button"
                      onClick={addOption}
                      disabled={optionCount >= 6}
                      className="eq-btn eq-btn-secondary eq-btn-sm"
                    >
                      + जोड़ें
                    </button>
                  </div>
                )}

                {questionType === "MATCH" ? (
                  <>
                    {/* Match the Following - Two Columns */}
                    <div style={{marginBottom: "20px"}}>
                      <h3 className="eq-match-column-header">कॉलम I — मिलान करने के लिए आइटम</h3>
                      <div className="eq-option-control" style={{marginBottom: "14px"}}>
                        <span className="eq-option-control-label">कॉलम I में आइटम (4–6)</span>
                        <button
                          type="button"
                          onClick={removeOption}
                          disabled={optionCount <= 4}
                          className="eq-btn eq-btn-secondary eq-btn-sm"
                        >
                          − हटाएं
                        </button>
                        <span className="eq-option-count-display">
                          {optionCount} आइटम &nbsp;({matchLabel("A")}–{matchLabel(MATCH_COLUMN_I_KEYS[optionCount - 1])})
                        </span>
                        <button
                          type="button"
                          onClick={addOption}
                          disabled={optionCount >= 6}
                          className="eq-btn eq-btn-secondary eq-btn-sm"
                        >
                          + जोड़ें
                        </button>
                      </div>
                      {formData.options
                        .filter(opt => ['A', 'B', 'C', 'D', 'E', 'F'].includes(opt.key))
                        .slice(0, optionCount)
                        .map(option => renderOptionWithImage(option, `कॉलम I - आइटम ${matchLabel(option.key)}`, 2, matchLabel(option.key)))
                      }
                    </div>

                    <div style={{marginBottom: "20px"}}>
                      <h3 className="eq-match-column-header">कॉलम II — मिलान के विकल्प</h3>
                      <div className="eq-match-tip">
                        <i className="bi bi-lightbulb"></i> <strong>सुझाव:</strong> प्रत्येक कॉलम II आइटम के दाईं ओर ड्रॉपडाउन का उपयोग करें।
                      </div>
                      {formData.options
                        .filter(opt => ['P', 'Q', 'R', 'S', 'T', 'U'].includes(opt.key))
                        .slice(0, optionCount)
                        .map((option, i) => {
                          const columnIKeys = formData.options
                            .filter(opt => ['A', 'B', 'C', 'D', 'E', 'F'].includes(opt.key))
                            .slice(0, optionCount)
                            .map(opt => opt.key);
                          
                          const usedColumnIKeys = Object.entries(matchMappings)
                            .filter(([colIIKey, colIKey]) => colIIKey !== option.key && colIKey)
                            .map(([_, colIKey]) => colIKey);
                          
                          const availableColumnIKeys = columnIKeys.filter(
                            colIKey => !usedColumnIKeys.includes(colIKey)
                          );
                          
                          return (
                            <div key={option.key} className="eq-match-row">
                              <div className="eq-match-row-option">
                                {renderOptionWithImage(option, `कॉलम II - आइटम ${matchLabel(option.key)}`, 2, matchLabel(option.key))}
                              </div>
                              <div className="eq-match-dropdown-col">
                                <span className="eq-match-dropdown-label">मेल खाता है:</span>
                                <select
                                  value={matchMappings[option.key] || ""}
                                  onChange={(e) => {
                                    setMatchMappings({
                                      ...matchMappings,
                                      [option.key]: e.target.value
                                    });
                                    if (errors.matchMappings) {
                                      setErrors({ ...errors, matchMappings: null });
                                    }
                                  }}
                                  className={`eq-match-select${matchMappings[option.key] ? " mapped" : ""}`}
                                >
                                  <option value="">चुनें...</option>
                                  {availableColumnIKeys.map(colIKey => (
                                    <option key={colIKey} value={colIKey}>
                                      आइटम {matchLabel(colIKey)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          );
                        })
                      }
                      {errors.matchMappings && (
                        <div className="eq-match-error">{errors.matchMappings}</div>
                      )}
                      {Object.keys(matchMappings).length > 0 && (
                        <div className="eq-match-mappings">
                          <strong>वर्तमान मिलान:</strong>
                          <div className="eq-match-mapping-chips">
                            {Object.entries(matchMappings)
                              .filter(([, colI]) => colI)
                              .sort(([, a], [, b]) => a.localeCompare(b))
                              .map(([colII, colI]) => (
                                <span key={colII} className="eq-match-chip">
                                  {matchLabel(colI)} → {matchLabel(colII)}
                                </span>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Regular options for SINGLE_CORRECT, MULTI_CORRECT, PARAGRAPH */}
                    <div className="eq-form-group">
                      <label className="eq-label">विकल्प (Options)</label>
                    </div>
                    {formData.options
                      .slice(0, optionCount)
                      .map(option => renderOptionWithImage(option, `विकल्प ${option.key}`, 2))
                    }
                  </>
                )}
              </>
            )}

            {renderFieldWithPreview("explanationHindi", "व्याख्या (हिंदी) (Optional)", 5)}
          </>
        )}

        {/* Correct Answer */}
        {["SINGLE_CORRECT", "MULTI_CORRECT", "PARAGRAPH"].includes(questionType) && (
          <div className="eq-form-group">
            <label className="eq-label">
              Correct Answer <span className="eq-required">*</span>
            </label>
            {questionType === "MULTI_CORRECT" ? (
              <div className="eq-checkbox-panel">
                <p className="eq-checkbox-hint">Select all correct options:</p>
                <p className="eq-checkbox-hint">
                  Partial marking (JEE Advanced): all correct options chosen = full marks; only some correct
                  options chosen and no wrong one = +1 per option on a 4-mark question (marks ÷ number of
                  options each); any wrong option = negative marks.
                </p>
                {formData.options.slice(0, optionCount).map((option) => (
                  <label key={option.key} className="eq-checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedAnswers.includes(option.key)}
                      onChange={() => handleCheckboxChange(option.key)}
                      className="eq-checkbox-input"
                    />
                    <span className="eq-checkbox-text">Option {option.key}</span>
                  </label>
                ))}
                {selectedAnswers.length > 0 && (
                  <div className="eq-selected-answers-tag">
                    Selected: {selectedAnswers.join(", ")}
                  </div>
                )}
              </div>
            ) : (
              <select
                name="correctAnswer"
                value={formData.correctAnswer}
                onChange={handleChange}
                className="eq-select"
              >
                {formData.options.slice(0, optionCount).map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.key}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* YouTube Solution Link */}
        <div className="eq-form-group">
          <label className="eq-label">
            YouTube Solution Link <span className="eq-label-optional">(optional)</span>
          </label>
          <input
            type="url"
            name="youtubeLink"
            value={formData.youtubeLink}
            onChange={handleChange}
            placeholder="e.g., https://www.youtube.com/watch?v=..."
            className="eq-input"
          />
          <span className="eq-hint">Enter the YouTube video link for solution explanation</span>
        </div>

        {/* Image Upload */}
        <div className="eq-form-group">
          <label className="eq-label">
            Question Image <span className="eq-label-optional">(optional)</span>
          </label>
          {formData.imagePreview ? (
            <div className="eq-question-image-preview">
              <img src={formData.imagePreview} alt="Question preview" />
              <button
                type="button"
                className="eq-btn eq-btn-danger eq-btn-sm"
                onClick={removeImage}
              >
                <i className="bi bi-trash3"></i> Remove image
              </button>
            </div>
          ) : (
            <input
              ref={questionImageInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleImageUpload}
              className="eq-file-input"
            />
          )}
        </div>

        {/* Form Actions */}
        <div className="eq-form-actions">
          <button
            type="button"
            className="eq-btn eq-btn-secondary"
            onClick={() => {
              if (sectionId) {
                navigate(`/admin/test-series/${seriesId}/section/${sectionId}/questions`);
              } else {
                navigate(`/admin/test-series`);
              }
            }}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="eq-btn eq-btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? <span className="btn-spinner"></span> : <i className="bi bi-check2"></i>}
            {isSubmitting ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditQuestion;
